import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import MultiSelectCheckbox from '../MultiSelectCheckbox';

interface MonthSelectorProps {
  selectedMonths: string[];
  onMonthsChange: (months: string[]) => void;
  availableMonths: string[];
  placeholder?: string;
  size?: 'small' | 'medium';
  minWidth?: string;
}

const monthShortcuts = [
  { label: 'Últimos 3 meses', months: 3 },
  { label: 'Últimos 6 meses', months: 6 },
  { label: 'Últimos 12 meses', months: 12 },
  { label: 'Últimos 18 meses', months: 18 },
  { label: 'Últimos 24 meses', months: 24 },
  { label: 'Todos los meses', months: -1 },
];

const MonthSelector: React.FC<MonthSelectorProps> = ({
  selectedMonths,
  onMonthsChange,
  availableMonths,
  placeholder = 'Seleccionar meses',
  size = 'small',
  minWidth = '200px',
}) => {
  const [useShortcuts, setUseShortcuts] = useState(false);
  const [shortcut, setShortcut] = useState('');

  const handleShortcutChange = (shortcutLabel: string) => {
    const shortcut = monthShortcuts.find((sc) => sc.label === shortcutLabel);
    if (!shortcut) return;

    if (shortcut.months === -1) {
      onMonthsChange([...availableMonths]);
    } else {
      const sortedMonths = [...availableMonths].sort((a, b) => {
        const dateA = new Date(a + '-01');
        const dateB = new Date(b + '-01');
        return dateB.getTime() - dateA.getTime();
      });

      const selectedMonths = sortedMonths.slice(0, shortcut.months);
      onMonthsChange(selectedMonths);
    }

    setShortcut(shortcutLabel);
  };

  const handleManualSelection = (months: string[]) => {
    onMonthsChange(months);
    setShortcut('');
  };

  const handleClearSelection = () => {
    onMonthsChange([]);
    setShortcut('');
  };

  const handleSelectAll = () => {
    onMonthsChange([...availableMonths]);
  };

  return (
    <div style={{ width: 'auto', minWidth }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <div style={{ flex: 1, minWidth: minWidth }}>
          {useShortcuts ? (
            <FormControl size={size} style={{ width: '100%' }}>
              <Select
                value={shortcut}
                onChange={(e) => handleShortcutChange(e.target.value)}
                displayEmpty
                style={{ width: '100%' }}
              >
                <MenuItem value="">
                  <em>Atajo de últimos meses</em>
                </MenuItem>
                {monthShortcuts.map((shortcut) => (
                  <MenuItem key={shortcut.label} value={shortcut.label}>
                    {shortcut.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <MultiSelectCheckbox
              options={availableMonths}
              selectedValues={selectedMonths}
              onSelectionChange={handleManualSelection}
              placeholder={placeholder}
              size={size}
              minWidth={minWidth}
            />
          )}
        </div>

        <Stack direction="column" spacing={1} justifyContent="center">
          <Button
            size="small"
            variant="outlined"
            onClick={handleSelectAll}
            style={{
              fontSize: '0.7rem',
              padding: '2px 8px',
              minWidth: '60px',
              height: '32px',
            }}
          >
            TODOS
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleClearSelection}
            style={{
              fontSize: '0.7rem',
              padding: '2px 8px',
              minWidth: '60px',
              height: '32px',
            }}
          >
            LIMPIAR
          </Button>
        </Stack>

        <Stack
          direction="column"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <Switch
            checked={useShortcuts}
            onChange={(e) => setUseShortcuts(e.target.checked)}
            size="small"
          />
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ fontSize: '0.7rem' }}
          >
            {useShortcuts ? 'Atajos' : 'Manual'}
          </Typography>
        </Stack>
      </Stack>
    </div>
  );
};

export default MonthSelector;
